'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { 
  getRegionColors, 
  provenceColors,
  mauritiusColors,
} from '../utils/regionColors';

export interface Region {
  id: string;
  name: string;
  country: string;
  description: string;
  image: string;
  propertyCount?: number;
  link: string;
}

export interface StoneInvestmentRegionShowcaseProps {
  title?: string;
  subtitle?: string;
  regions?: Region[];
  locale?: string;
}

/**
 * Stone Investment Region Showcase
 * 
 * Displays the two key markets: Provence and Mauritius
 * with elegant imagery and minimal text
 */
export function StoneInvestmentRegionShowcase({
  title = 'Our Markets',
  subtitle = 'Exceptional properties in two of the world\'s most desirable destinations',
  regions,
  locale = 'en',
}: StoneInvestmentRegionShowcaseProps) {
  // Default regions for Stone Investment
  const defaultRegions: Region[] = [
    {
      id: 'provence',
      name: 'Provence',
      country: 'France',
      description: 'From the lavender fields of Luberon to the historic charm of Marseille, discover exceptional properties in the heart of Provence.',
      image: 'https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?w=1200&h=800&fit=crop',
      link: `/${locale}/properties?region=provence`,
    },
    {
      id: 'mauritius',
      name: 'Mauritius',
      country: 'Indian Ocean',
      description: 'Luxury beachfront villas and exclusive PDS developments on the pristine shores of this tropical paradise.',
      image: 'https://images.unsplash.com/photo-1580541832626-2a7131ee809f?w=1200&h=800&fit=crop',
      link: `/${locale}/properties?region=mauritius`,
    },
  ];

  const displayRegions = regions || defaultRegions;

  return (
    <section className="py-24 lg:py-32 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 lg:mb-20 text-center"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-px bg-white/30" />
            <span 
              className="text-xs tracking-[0.3em] uppercase text-white/50"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Destinations
            </span>
            <div className="w-16 h-px bg-white/30" />
          </div>
          
          <h2 
            className="text-4xl lg:text-5xl xl:text-6xl text-white tracking-[-0.02em] mb-6"
            style={{ 
              fontFamily: "'Cormorant Garamond', 'Georgia', serif",
              fontWeight: 300,
            }}
          >
            {title}
          </h2>
          
          <p 
            className="text-white/60 text-base lg:text-lg max-w-2xl mx-auto"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {subtitle}
          </p>
        </motion.div>

        {/* Regions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {displayRegions.map((region, index) => {
            // Get region-specific colors
            const regionColors = region.id === 'provence' 
              ? provenceColors 
              : region.id === 'mauritius' 
                ? mauritiusColors 
                : getRegionColors('unknown');
            const flag = region.id === 'provence' 
              ? '🇫🇷' 
              : region.id === 'mauritius' 
                ? '🇲🇺' 
                : '🌍';

            return (
              <motion.div
                key={region.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <Link href={region.link} className="group block relative">
                  {/* Image Container */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {/* Region Accent Bar - Left side */}
                    <div 
                      className="absolute top-0 left-0 bottom-0 w-1.5 z-20"
                      style={{ background: regionColors.gradient }}
                    />

                    <Image
                      src={region.image}
                      alt={`${region.name}, ${region.country}`}
                      fill
                      className="object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 group-hover:scale-105"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    
                    {/* Region Badge - Top left */}
                    <div className="absolute top-6 left-6 z-10">
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 backdrop-blur-sm"
                        style={{ 
                          backgroundColor: 'rgba(0,0,0,0.6)',
                          borderLeft: `2px solid ${regionColors.primary}`,
                        }}
                      >
                        <span className="text-sm">{flag}</span>
                        <span 
                          className="text-[9px] tracking-[0.2em] uppercase text-white"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        >
                          {region.id === 'provence' ? 'Provence' : region.id === 'mauritius' ? 'Mauritius' : region.name}
                        </span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-10">
                      {/* Country Tag with Region Color */}
                      <div className="flex items-center gap-2 mb-3">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: regionColors.primary }}
                        />
                        <span 
                          className="text-[10px] tracking-[0.35em] uppercase"
                          style={{ 
                            fontFamily: "'DM Sans', sans-serif",
                            color: regionColors.secondary,
                          }}
                        >
                          {region.country}
                        </span>
                      </div>
                      
                      {/* Region Name */}
                      <h3 
                        className="text-3xl lg:text-4xl text-white mb-4 group-hover:text-white/90 transition-colors duration-300"
                        style={{ 
                          fontFamily: "'Cormorant Garamond', 'Georgia', serif",
                          fontWeight: 400,
                        }}
                      >
                        {region.name}
                      </h3>
                      
                      {/* Description */}
                      <p 
                        className="text-white/60 text-sm leading-relaxed max-w-md mb-6"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                      >
                        {region.description}
                      </p>
                      
                      {/* CTA with Region Color */}
                      <div 
                        className="inline-flex items-center gap-3 transition-colors duration-300"
                        style={{ 
                          fontFamily: "'DM Sans', sans-serif",
                          color: regionColors.secondary,
                        }}
                      >
                        <span className="text-xs tracking-[0.2em] uppercase group-hover:text-white transition-colors">
                          Explore Properties
                        </span>
                        <div 
                          className="w-10 h-10 border flex items-center justify-center transition-all duration-300 group-hover:bg-white/10"
                          style={{ borderColor: regionColors.primary + '80' }}
                        >
                          <svg 
                            className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                            strokeWidth={1}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Property Count Badge with Region Color */}
                    {region.propertyCount && (
                      <div className="absolute top-6 right-6">
                        <span 
                          className="text-xs tracking-widest backdrop-blur-sm px-4 py-2"
                          style={{ 
                            fontFamily: "'DM Sans', sans-serif",
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: regionColors.secondary,
                            borderBottom: `2px solid ${regionColors.primary}`,
                          }}
                        >
                          {region.propertyCount} Properties
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
